// @DECREPIT
import { AxiosRequestConfig } from 'axios';
import reporter from '@/utils/report';
import { fetch } from './base';
import {
  BaseRes,
  createMultipartUploadRes,
  uploadPartRes,
  validateDownloadRes,
  ReportUserReadMsgParams,
  ChatEndParams,
  QueryQueueAvailabilityParams,
  SubmitWebformParams,
  ICheckIsCounldInBoundParams,
} from '@/interfaces';
import { getTrans } from '@/utils/tools';
import { HISTORY_TRANSIFY_KEY } from '@/utils/constants';
import { AGENT_CHANNEL } from '@/config/constants';
import { getConfig } from '@/config/core';
import { webformNewApiCheckMultipleActionParams } from '@/utils/common';
import { handleRequest } from '@/utils';
import { COUNTRY, getHelpHost } from '../../utils/locale';
import API from '@/services';

const HEADER_SESSION_KEY_NAME = 'Session-Key';

const API_ENV = (() => {
  let result = '';
  const environment = ENVIRONMENT === 'development' ? 'test' : ENVIRONMENT;
  if (environment && environment !== 'live') {
    result = `.${environment}`;
  }
  return result;
})();

// img
export const postImg = async (file: File): Promise<{ url: string; thumbnailUrl: string } | undefined> => {
  reporter.apiBatchReport({
    type: 'count',
    value: {
      label: 'channel_chat_file_proxy_upload_total',
      extend_1: 'img',
    },
  });
  const formData = new FormData();
  formData.append('images', file);
  formData.append('call_type', 'FE');
  formData.append('region', COUNTRY);
  formData.append('app_name', 'cs-fe');
  formData.append('bucket_name', 'channel-client-public');
  formData.append('is_thumbnail', 'true');
  let result;
  try {
    const resp: any = await fetch.post(
      `https://cs${API_ENV}.shopee.com/api/cs/fileproxy/noauth/v5/upload/images`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }},
    );
    if (!resp) {
      throw new Error('empty response');
    }
    if (resp && resp?.images?.length > 0 && resp.images[0].success) {
      const tmp = resp.images[0];
      result = {
        url: `${tmp.url}?call_type=FE&app_name=cs-fe`,
        thumbnailUrl: `${tmp.thumbnail_url}?call_type=FE&app_name=cs-fe`,
      };
    }
  } catch (err) {
    reporter.apiBatchReport({
      type: 'count',
      value: {
        label: 'channel_chat_file_proxy_upload_fail_total',
        extend_1: 'img',
      },
    });
  }
  return result;
};

export const chatEnd = async (sessionKey: string, params: ChatEndParams = {}): Promise<BaseRes> => {
  return await fetch.post(
    '/chat_end',
    {
      reason: 'client',
      ...params,
    },
    { headers: { [HEADER_SESSION_KEY_NAME]: sessionKey }},
  );
};

export const postChatHeartbeat = async (userId: string): Promise<BaseRes> => {
  return await fetch.post('/heartbeat', { id: String(userId) });
};

export const uploadFile = async (
  file: File,
  config?: AxiosRequestConfig,
): Promise<{ url: string; fileId: string } | Error> => {
  reporter.apiBatchReport({
    type: 'count',
    value: {
      label: 'channel_chat_file_proxy_upload_total',
      extend_1: 'file',
    },
  });
  const formData = new FormData();
  formData.append('file', file);
  formData.append('call_type', 'FE');
  formData.append('region', COUNTRY);
  formData.append('app_name', 'cs-fe');
  formData.append('bucket_name', 'channel-client-public');
  let result;
  try {
    const resp: any = await fetch.post(
      `https://cs${API_ENV}.shopee.com/api/cs/fileproxy/noauth/v5/upload/file`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, ...config },
    );
    if (!resp) {
      throw new Error('empty response');
    }
    const { file_id, url } = resp;
    result = {
      fileId: file_id,
      url: `${url}?call_type=FE&app_name=cs-fe`,
    };
  } catch (err) {
    reporter.apiBatchReport({
      type: 'count',
      value: {
        label: 'channel_chat_file_proxy_upload_fail_total',
        extend_1: 'file',
      },
    });
    result = err as Error;
  }
  return result;
};

export const createMultipartUpload = async (
  file_name: string,
  file_size: number,
): Promise<createMultipartUploadRes | Error> => {
  return await fetch
    .post('/create_multipart_upload', { file_name, file_size })
    .then((res: any) => {
      if (res.code !== 0) {
        const error = new Error(res.msg || `Request failed with data code ${res.code || 'UNKNOWN'}`);
        return error;
      }
      return res;
    })
    .catch((err) => {
      return err;
    });
};

export const uploadPart = async (
  upload_id: string,
  file_key: string,
  part_num: number,
  blob: Blob,
  config?: AxiosRequestConfig,
): Promise<uploadPartRes | Error> => {
  const search = new URLSearchParams({
    upload_id,
    file_key,
    part_num: part_num.toString(),
  });
  return await fetch
    .post('/upload_part?' + search.toString(), blob, config)
    .then((res: any) => {
      if (res.code !== 0) {
        const error = new Error(res.msg || `Request failed with data code ${res.code || 'UNKNOWN'}`);
        return error;
      }
      return res;
    })
    .catch((err) => {
      return err;
    });
};

export const completeMultipartUpload = async (
  upload_id: string,
  file_key: string,
  completed_parts: {
    etag: string;
    part_num: string;
  }[],
): Promise<{
  code: number;
  msg: string;
  data: {
    file_url: string;
  };
}> => {
  return await fetch
    .post('/complete_multipart_upload', {
      upload_id,
      file_key,
      completed_parts,
    })
    .then((res: any) => {
      if (res.code !== 0) {
        const error = new Error(res.msg || `Request failed with data code ${res.code || 'UNKNOWN'}`);
        return error;
      }
      return res;
    })
    .catch((err) => {
      return err;
    });
};

export const abortMultipartUpload = async (upload_id: string, file_key: string): Promise<BaseRes | Error> => {
  return await fetch
    .post('/abort_multipart_upload', {
      upload_id,
      file_key,
    })
    .then((res: any) => {
      if (res.code !== 0) {
        const error = new Error(res.msg || `Request failed with data code ${res.code || 'UNKNOWN'}`);
        return error;
      }
      return res;
    })
    .catch((err) => {
      return err;
    });
};

export const validateDownload = async (file_key: string): Promise<validateDownloadRes> => {
  return await fetch
    .get('/validate_download', { params: { file_key }})
    .then((res: any) => {
      if (res.code !== 0) {
        const error = new Error(
          getTrans(HISTORY_TRANSIFY_KEY.FILE_IS_INVALID) || `Request failed with data code ${res.code || 'UNKNOWN'}`,
        );
        return error;
      }
      return res;
    })
    .catch((err) => {
      return err;
    });
};

export async function getVideoToken(sessionKey: string, amount: number) {
  const { getVideoTokenParams = {}} = getConfig();
  return await fetch
    .post(
      '/video_pre_upload',
      { amount, ...getVideoTokenParams },
      { headers: { [HEADER_SESSION_KEY_NAME]: sessionKey }},
    )
    .then((res: any) => {
      if (res.code !== 0) {
        const error = new Error();
        return error;
      }
      return res;
    })
    .catch((err) => {
      return err;
    });
}

export const getEmergencyTemplate = async (params: { channel?: AGENT_CHANNEL }): Promise<any> => {
  return await fetch.get('/emergency/template', { params });
};

export const reportUserReadMsgOffset = async (params: ReportUserReadMsgParams): Promise<BaseRes<any>> => {
  return await fetch.post('/report_user_read_msg_offset', params);
};

export const queryQueueAvailability = async (params: QueryQueueAvailabilityParams): Promise<any> => {
  return await (API as { queryQueueAvailability: Function })
    .queryQueueAvailability({ ...params })
    .catch((err: any) => {});
};
export const submitWebform = async (params: any): Promise<any> => {

  return (
    await (API as { submitWebform: Function })
      .submitWebform({ api_key: 'inhouse_submit_webform', ...params })
      .catch((err: any) => {})
  );
};

export const isMultipleWebform = async (params: any): Promise<any> => {
  return await (API as { isMultipleWebform: Function })
    .isMultipleWebform({ api_key: 'check_multiple_action', ...params })
    .catch((err: any) => {});
};

export const isMultipleWebformNewApi = async (params: any): Promise<any> => {
  if (params?.message_extra?.task_bot_action) {
    params.message_extra.task_bot_action = {
      ...webformNewApiCheckMultipleActionParams,
      ...params.message_extra.task_bot_action,
    };
  }
  return await handleRequest('postMessage', params).catch((err: any) => {});
};

export const consumptionDatasByCsFillToken = async (params: any): Promise<BaseRes<any>> => {
  return await fetch
    .get('/api/inhouse/bff/v1/data/get/batch_one', {
      params: {
        ...params,
        module: 2,
      },
      baseURL: getHelpHost(),
      headers: { origin: window.location.href },
      transformRequest: (data, headers) => {
        delete headers?.common?.['shopee-region'];
        return data;
      },
    })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      return err;
    });
};

export const checkIsCouldInbound = async (params: ICheckIsCounldInBoundParams): Promise<BaseRes<any>> => {
  return (fetch
    .put('/session/pre_check_inbound', params, { baseURL: '/chat/v2' })
    .then((res: any) => {
      if (res.code !== 0) {
        const error = new Error(res.msg || `Request failed with data code ${res.code || 'UNKNOWN'}`);
        return error;
      }
      return res;
    })
    .catch((err) => {
      return err;
    }));
};
